/**
 * YUMIX — YumixTip (Iter. 393)
 *
 * Generic, reusable inline tip component. Renders a small lightbulb icon
 * that the user can click to open a popover explaining a concept. Once
 * the user clicks "Compris", the icon dims (visual cue that it has been
 * acknowledged) and the popover never auto-opens again.
 *
 * Unlike <FeatureHint> (which WRAPS an input and pops a one-time bubble),
 * YumixTip is a STANDALONE button you drop next to any label/title to make
 * coaching context available on demand without taking screen space.
 *
 * Usage:
 *   <Label>Tempo <YumixTip id="exercise-tempo" title="Tempo" text="…" /></Label>
 *
 * Props:
 *   - id     : unique persistence key (required) — namespace with "yumix-tip-".
 *   - title  : short header (string, required).
 *   - text   : explanation body (string or JSX, required).
 *   - side   : popover side (default 'top'). 'top' | 'right' | 'bottom' | 'left'.
 *   - align  : popover align (default 'center').
 *   - size   : 'sm' | 'md' (default 'sm') — icon visual size.
 *   - tone   : 'orange' | 'primary' (default 'orange') — icon accent.
 *   - language : optional, for the "Compris" CTA label (defaults to FR).
 */
import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Lightbulb, X } from 'lucide-react';
import { useFeatureDiscovery } from '../context/FeatureDiscoveryContext';

const SIZES = {
  sm: { btn: 'w-5 h-5', icon: 'w-3 h-3' },
  md: { btn: 'w-6 h-6', icon: 'w-3.5 h-3.5' },
};

const TONES = {
  orange:  { accent: '#fb923c', ring: 'rgba(251,146,60,0.5)' },
  primary: { accent: 'hsl(var(--primary))', ring: 'rgba(251,146,60,0.5)' },
};

const DISMISS_LABEL = { fr: 'Compris', en: 'Got it', es: 'Entendido' };
const CLOSE_LABEL = { fr: 'Fermer', en: 'Close', es: 'Cerrar' };

const YumixTip = ({
  id,
  title,
  text,
  side = 'top',
  align = 'center',
  size = 'sm',
  tone = 'orange',
  language = 'fr',
}) => {
  const { hasSeen, markSeen } = useFeatureDiscovery();
  // Namespace the id so YumixTip persistence doesn't collide with the legacy
  // FeatureHint ids that already live in `yumix_seen_tips_v1`.
  const persistId = `yumix-tip-${id}`;
  const acknowledged = hasSeen(persistId);
  const [open, setOpen] = useState(false);

  const sizing = SIZES[size] || SIZES.sm;
  const palette = TONES[tone] || TONES.orange;

  const handleAck = () => {
    markSeen(persistId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={typeof title === 'string' ? title : 'Conseil'}
          data-testid={`yumix-tip-${id}`}
          data-acknowledged={acknowledged ? 'true' : 'false'}
          className={`shrink-0 inline-flex items-center justify-center rounded-full ${sizing.btn} transition-all`}
          style={{
            background: acknowledged ? 'transparent' : `${palette.accent}20`,
            color: palette.accent,
            opacity: acknowledged ? 0.45 : 1,
            boxShadow: acknowledged ? 'none' : `0 0 8px ${palette.ring}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Lightbulb className={sizing.icon} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-72 p-0 border-primary/50 bg-card shadow-lg shadow-primary/20"
        data-testid={`yumix-tip-content-${id}`}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-3 space-y-2">
          <div className="flex items-start gap-2">
            <div
              className="mt-0.5 shrink-0"
              style={{ color: palette.accent, filter: `drop-shadow(0 0 4px ${palette.ring})` }}
            >
              <Lightbulb className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="font-heading uppercase text-[11px] tracking-wider"
                style={{ color: palette.accent }}
              >
                {title}
              </p>
              <div className="text-xs text-foreground mt-1 leading-relaxed">{text}</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="shrink-0 text-muted-foreground hover:text-foreground -mt-0.5 -mr-0.5"
              aria-label={CLOSE_LABEL[language] || CLOSE_LABEL.fr}
              data-testid={`yumix-tip-close-${id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleAck}
              className="h-7 text-[11px] uppercase tracking-wider text-primary hover:bg-primary/10"
              data-testid={`yumix-tip-ack-${id}`}
            >
              {DISMISS_LABEL[language] || DISMISS_LABEL.fr}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default YumixTip;
